import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid2 as Grid,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  Autocomplete,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

const SalesCustomers = () => {
  const navigate = useNavigate();
  const navigationBlockedRef = useRef(false);

  // First card state for searching and adding contractors
  const [firstCard, setFirstCard] = useState({
    licenseNumberInput: "",
    nameInput: "",
    selectedContractor: null,
    contractorOptions: [],
    loadingOptions: false,
    showDetails: false,
    licenseNumber: "",
    dbaName: "",
    firstName: "",
    lastName: "",
    middleName: "",
    license_type: "",
    rank: "",
    county: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    email: "",
    planType: "Free",
    reg_date: {
      effective: "",
      expiration: "",
    },
    status: {
      secondary: "",
    },
  });

  // List of added customers for DataGrid
  const [addedCustomers, setAddedCustomers] = useState([]);
  const [nextId, setNextId] = useState(1);
  const [submitError, setSubmitError] = useState(null);
  const [licenseNumberError, setLicenseNumberError] = useState(null);
  const [checkingLicense, setCheckingLicense] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const planTypes = ["Free", "Premium", "Rockstar"];
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const searchTimeoutsRef = useRef({});

  // Debounce search function
  const searchContractors = useCallback(async (searchTerm, searchType) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFirstCard((prev) => ({
        ...prev,
        contractorOptions: [],
        loadingOptions: false,
      }));
      return;
    }

    setFirstCard((prev) => ({ ...prev, loadingOptions: true }));

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/search-contractors`,
        {
          params: {
            search_term: searchTerm,
            search_type: searchType,
          },
          withCredentials: true,
        },
      );

      if (res.data.success) {
        const options = res.data.data || [];
        setFirstCard((prev) => ({
          ...prev,
          contractorOptions: options,
          loadingOptions: false,
        }));
      } else {
        setFirstCard((prev) => ({
          ...prev,
          contractorOptions: [],
          loadingOptions: false,
        }));
      }
    } catch (error) {
      console.error("Error searching contractors:", error);
      setFirstCard((prev) => ({
        ...prev,
        contractorOptions: [],
        loadingOptions: false,
      }));
    }
  }, []);

  const handleAddToGrid = async () => {
    // Validate required fields
    if (
      !firstCard.licenseNumber.trim() ||
      !firstCard.email.trim() ||
      !firstCard.address1.trim() ||
      !firstCard.city.trim() ||
      !firstCard.state.trim() ||
      !firstCard.zip.trim() ||
      !firstCard.county.trim()
    ) {
      setSubmitError(
        "License number, email, address, city, state, zip, and county are required",
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(firstCard.email)) {
      setSubmitError("Invalid email format");
      return;
    }

    // Check for duplicate license number in addedCustomers
    const duplicateLicense = addedCustomers.some(
      (customer) =>
        customer.licenseNumber?.toUpperCase().trim() ===
        firstCard.licenseNumber.toUpperCase().trim(),
    );
    if (duplicateLicense) {
      setSubmitError("This license number has already been added");
      return;
    }

    // Check for duplicate email in addedCustomers
    const duplicateEmail = addedCustomers.some(
      (customer) =>
        customer.email?.toLowerCase().trim() ===
        firstCard.email.toLowerCase().trim(),
    );
    if (duplicateEmail) {
      setSubmitError("This email address has already been added");
      return;
    }

    // Check if email exists in database
    const emailExists = await checkEmailExists(firstCard.email);
    if (emailExists) {
      // Error is already set in checkEmailExists
      return;
    }

    setSubmitError(null);
    setEmailError(null);

    // Add to DataGrid
    const newCustomer = {
      id: nextId,
      licenseNumber: firstCard.licenseNumber,
      dbaName: firstCard.dbaName,
      first_name: firstCard.firstName,
      last_name: firstCard.lastName,
      address_parts: {
        part_1: firstCard.address1,
        part_2: firstCard.address2,
        city: firstCard.city,
        state: firstCard.state,
        zip: firstCard.zip,
      },
      email: firstCard.email,
      planType: firstCard.planType,
      name:
        firstCard.dbaName ||
        `${firstCard.firstName} ${firstCard.lastName}`.trim() ||
        "N/A",
    };

    setAddedCustomers((prev) => [...prev, newCustomer]);
    setNextId((prev) => prev + 1);
    setSubmitError(null); // Clear any errors after successful add

    // Clear first card
    setFirstCard({
      licenseNumberInput: "",
      nameInput: "",
      selectedContractor: null,
      contractorOptions: [],
      loadingOptions: false,
      showDetails: false,
      licenseNumber: "",
      dbaName: "",
      firstName: "",
      lastName: "",
      middleName: "",
      license_type: "",
      rank: "",
      county: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      email: "",
      planType: "Free",
      reg_date: {
        effective: "",
        expiration: "",
      },
      status: {
        secondary: "",
      },
    });
  };

  const handleRemoveFromGrid = (customerId) => {
    setAddedCustomers((prev) => prev.filter((c) => c.id !== customerId));
  };

  const handleSubmitCustomers = async () => {
    if (addedCustomers.length === 0) {
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);

    try {
      // Transform addedCustomers to match backend expected format
      const customersData = addedCustomers.map((customer) => ({
        id: customer.id,
        licenseNumber: customer.licenseNumber,
        dbaName: customer.dbaName,
        first_name: customer.first_name,
        last_name: customer.last_name,
        address_parts: {
          part_1: customer.address_parts.part_1,
          part_2: customer.address_parts.part_2 || "",
          city: customer.address_parts.city,
          state: customer.address_parts.state,
          zip: customer.address_parts.zip,
          country: customer.address_parts.country || "US",
        },
        email: customer.email,
        planType: customer.planType,
        name: customer.name,
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/submit-customers`,
        { customers: customersData },
        { withCredentials: true },
      );

      if (response.data.success) {
        // Clear the added customers list
        setAddedCustomers([]);
        setNextId(1);
        setSubmitError(null);

        // Show success message (you can use a snackbar or alert)
        alert(
          `Successfully submitted ${response.data.results?.length || 0} customers!${
            response.data.errors?.length > 0
              ? `\n\nErrors: ${response.data.errors.join("\n")}`
              : ""
          }`,
        );
      } else {
        setSubmitError(response.data.message || "Failed to submit customers");
      }
    } catch (error) {
      console.error("Error submitting customers:", error);
      setSubmitError(
        error.response?.data?.message ||
          "Failed to submit customers. Please try again.",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // Check if license number already exists
  const checkLicenseExists = async (licenseNumber) => {
    if (!licenseNumber || !licenseNumber.trim()) {
      return false;
    }

    const licenseUpper = licenseNumber.trim().toUpperCase();

    // First check if it exists in addedCustomers
    const existsInAdded = addedCustomers.some(
      (customer) =>
        customer.licenseNumber?.toUpperCase().trim() === licenseUpper,
    );

    if (existsInAdded) {
      setLicenseNumberError("This license number has already been added");
      return true;
    }

    // Then check via API if it exists in database
    try {
      setCheckingLicense(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/check-license-exists`,
        {
          license_number: licenseUpper,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data && response.data.success && response.data.exists) {
        setLicenseNumberError(
          "This license number is already registered by another user",
        );
        setCheckingLicense(false);
        return true;
      }

      setLicenseNumberError(null);
      setCheckingLicense(false);
      return false;
    } catch (error) {
      console.error("Error checking license:", error);
      // On API error, show error and prevent proceeding
      setLicenseNumberError(
        "Unable to verify license number. Please try again.",
      );
      setCheckingLicense(false);
      return true; // Return true to prevent proceeding on error
    }
  };

  // Check if email already exists
  const checkEmailExists = async (email) => {
    if (!email || !email.trim()) {
      return false;
    }

    const emailLower = email.trim().toLowerCase();

    // First check if it exists in addedCustomers
    const existsInAdded = addedCustomers.some(
      (customer) => customer.email?.toLowerCase().trim() === emailLower,
    );

    if (existsInAdded) {
      setEmailError("This email address has already been added");
      return true;
    }

    // Then check via API if it exists in database
    try {
      setCheckingEmail(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/check-email-exists`,
        {
          email: emailLower,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data && response.data.success && response.data.exists) {
        setEmailError(
          "This email address is already registered by another user",
        );
        setCheckingEmail(false);
        return true;
      }

      setEmailError(null);
      setCheckingEmail(false);
      return false;
    } catch (error) {
      console.error("Error checking email:", error);
      // On API error, show error and prevent proceeding
      setEmailError("Unable to verify email address. Please try again.");
      setCheckingEmail(false);
      return true; // Return true to prevent proceeding on error
    }
  };

  // Block navigation when there are unsaved customers
  useEffect(() => {
    const hasUnsavedData = addedCustomers.length > 0;

    // Handle browser/tab close or refresh
    const handleBeforeUnload = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers require a return value
      }
    };

    if (hasUnsavedData) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [addedCustomers.length]);

  // Intercept navigation attempts from SideNavItem
  useEffect(() => {
    if (addedCustomers.length === 0) {
      // Clean up when no unsaved data
      delete window.__salesCustomersHasUnsavedData;
      delete window.__salesCustomersCheckNavigation;
      return;
    }

    // Expose check function for SideNavItem to use
    window.__salesCustomersHasUnsavedData = true;
    window.__salesCustomersCheckNavigation = (path) => {
      const currentPath = window.location.pathname;

      // Only intercept if navigating to a different page
      if (path !== currentPath && !navigationBlockedRef.current) {
        setPendingNavigation(() => () => {
          navigationBlockedRef.current = true;
          navigate(path);
          // Reset after navigation completes
          setTimeout(() => {
            navigationBlockedRef.current = false;
          }, 100);
        });
        setShowLeaveWarning(true);
        return false; // Block navigation
      }
      return true; // Allow navigation
    };

    return () => {
      delete window.__salesCustomersHasUnsavedData;
      delete window.__salesCustomersCheckNavigation;
    };
  }, [addedCustomers.length, navigate]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    if (addedCustomers.length === 0) {
      return;
    }

    const handlePopState = (e) => {
      if (!navigationBlockedRef.current) {
        // Prevent the navigation by pushing current state back
        window.history.pushState(null, "", window.location.pathname);
        setShowLeaveWarning(true);
        setPendingNavigation(() => () => {
          navigationBlockedRef.current = true;
          window.history.back();
          setTimeout(() => {
            navigationBlockedRef.current = false;
          }, 100);
        });
      }
    };

    // Push a state to enable popstate detection
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [addedCustomers.length]);

  // Handle leave confirmation
  const handleConfirmLeave = () => {
    navigationBlockedRef.current = true;
    setShowLeaveWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
      // Reset after a short delay to allow navigation
      setTimeout(() => {
        navigationBlockedRef.current = false;
      }, 100);
    } else {
      // If no pending navigation, allow any future navigation
      setTimeout(() => {
        navigationBlockedRef.current = false;
      }, 100);
    }
  };

  const handleCancelLeave = () => {
    setShowLeaveWarning(false);
    setPendingNavigation(null);
  };

  // DataGrid columns
  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "licenseNumber",
      headerName: "License Number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "planType",
      headerName: "Plan Type",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleRemoveFromGrid(params.row.id)}
          color="error"
          disabled={submitLoading}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: smDown ? 1 : 2 }}>
      {/* Leave Warning Dialog */}
      <Dialog
        open={showLeaveWarning}
        onClose={handleCancelLeave}
        aria-labelledby="leave-warning-dialog-title"
        aria-describedby="leave-warning-dialog-description"
      >
        <DialogTitle id="leave-warning-dialog-title">Leave Page?</DialogTitle>
        <DialogContent>
          <DialogContentText id="leave-warning-dialog-description">
            You have unsaved customers. Are you sure you want to leave this
            page? All unsaved data will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLeave}
            color="error"
            variant="contained"
          >
            Leave Page
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
          Add Customers
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search by license number or name to prefill customer information, then
          add email and plan type
        </Typography>
      </Box>

      {/* Error Alert */}
      {submitError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}

      {/* First Card - Search and Add */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {/* License Number and Name Autocomplete - Side by Side */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" align="center" color="primary">
                Search for Contractor
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                disabled={firstCard.showDetails || submitLoading}
                options={firstCard.contractorOptions}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : option.license_number || ""
                }
                isOptionEqualToValue={(option, value) =>
                  typeof option === "string"
                    ? option === value
                    : option.id === value?.id ||
                      option.license_number === value?.license_number
                }
                loading={firstCard.loadingOptions}
                onInputChange={(event, newInputValue) => {
                  const upperValue = newInputValue.toUpperCase();
                  setLicenseNumberError(null); // Clear error when user types
                  setFirstCard((prev) => ({
                    ...prev,
                    licenseNumberInput: upperValue,
                    nameInput: "",
                    selectedContractor: null,
                    showDetails: false,
                    licenseNumber: "",
                    dbaName: "",
                    firstName: "",
                    lastName: "",
                    middleName: "",
                    license_type: "",
                    rank: "",
                    county: "",
                    address1: "",
                    address2: "",
                    city: "",
                    state: "",
                    zip: "",
                    country: "",
                    email: "",
                    contractorOptions: [],
                  }));
                  const timeoutKey = "license";
                  if (searchTimeoutsRef.current[timeoutKey]) {
                    clearTimeout(searchTimeoutsRef.current[timeoutKey]);
                  }
                  searchTimeoutsRef.current[timeoutKey] = setTimeout(() => {
                    if (upperValue && upperValue.length >= 2) {
                      searchContractors(upperValue, "license");
                    } else {
                      setFirstCard((prev) => ({
                        ...prev,
                        contractorOptions: [],
                      }));
                    }
                    delete searchTimeoutsRef.current[timeoutKey];
                  }, 300);
                }}
                onChange={(event, newValue) => {
                  setLicenseNumberError(null); // Clear error when selection changes
                  if (newValue && typeof newValue !== "string") {
                    const licenseNum = newValue.license_number || "";
                    const nameInput =
                      newValue.dba_name && newValue.primary_name
                        ? `${newValue.dba_name} (${newValue.primary_name})`
                        : newValue.dba_name || newValue.primary_name || "";
                    setFirstCard((prev) => ({
                      ...prev,
                      selectedContractor: newValue,
                      licenseNumberInput: licenseNum,
                      licenseNumber: licenseNum,
                      nameInput: nameInput,
                    }));
                  } else if (newValue === null) {
                    setFirstCard((prev) => ({
                      ...prev,
                      selectedContractor: null,
                      licenseNumberInput: "",
                      licenseNumber: "",
                      nameInput: "",
                    }));
                  }
                }}
                value={firstCard.selectedContractor || null}
                inputValue={
                  firstCard.selectedContractor
                    ? firstCard.selectedContractor.license_number || ""
                    : firstCard.licenseNumberInput || ""
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={
                      typeof option === "string"
                        ? option
                        : option.id || option.license_number
                    }
                  >
                    {typeof option === "string"
                      ? option
                      : option.license_number || ""}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="License Number"
                    size="large"
                    error={!!licenseNumberError}
                    helperText={licenseNumberError || " "}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {firstCard.loadingOptions || checkingLicense ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                disabled={firstCard.showDetails || submitLoading}
                options={firstCard.contractorOptions}
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  if (option.dba_name && option.primary_name) {
                    return `${option.dba_name} (${option.primary_name})`;
                  }
                  return option.dba_name || option.primary_name || "";
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof option === "string"
                    ? option === value
                    : option.id === value?.id ||
                      option.license_number === value?.license_number
                }
                loading={firstCard.loadingOptions}
                onInputChange={(event, newInputValue) => {
                  const upperValue = newInputValue.toUpperCase();
                  setFirstCard((prev) => ({
                    ...prev,
                    nameInput: upperValue,
                    licenseNumberInput: "",
                    selectedContractor: null,
                    showDetails: false,
                    licenseNumber: "",
                    dbaName: "",
                    firstName: "",
                    lastName: "",
                    middleName: "",
                    license_type: "",
                    rank: "",
                    county: "",
                    address1: "",
                    address2: "",
                    city: "",
                    state: "",
                    zip: "",
                    country: "",
                    email: "",
                    reg_date: {
                      effective: "",
                      expiration: "",
                    },
                    status: {
                      secondary: "",
                    },
                    contractorOptions: [],
                  }));
                  const timeoutKey = "name";
                  if (searchTimeoutsRef.current[timeoutKey]) {
                    clearTimeout(searchTimeoutsRef.current[timeoutKey]);
                  }
                  searchTimeoutsRef.current[timeoutKey] = setTimeout(() => {
                    if (upperValue && upperValue.length >= 2) {
                      searchContractors(upperValue, "name");
                    } else {
                      setFirstCard((prev) => ({
                        ...prev,
                        contractorOptions: [],
                      }));
                    }
                    delete searchTimeoutsRef.current[timeoutKey];
                  }, 300);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue !== "string") {
                    const licenseNum = newValue.license_number || "";
                    const nameInput =
                      newValue.dba_name && newValue.primary_name
                        ? `${newValue.dba_name} (${newValue.primary_name})`
                        : newValue.dba_name || newValue.primary_name || "";
                    setFirstCard((prev) => ({
                      ...prev,
                      selectedContractor: newValue,
                      licenseNumberInput: licenseNum,
                      licenseNumber: licenseNum,
                      nameInput: nameInput,
                    }));
                  } else if (newValue === null) {
                    setFirstCard((prev) => ({
                      ...prev,
                      selectedContractor: null,
                      licenseNumberInput: "",
                      licenseNumber: "",
                      nameInput: "",
                    }));
                  }
                }}
                value={firstCard.selectedContractor || null}
                inputValue={firstCard.nameInput || ""}
                renderOption={(props, option) => {
                  const label =
                    typeof option === "string"
                      ? option
                      : option.dba_name && option.primary_name
                        ? `${option.dba_name} (${option.primary_name})`
                        : option.dba_name || option.primary_name || "";
                  return (
                    <li
                      {...props}
                      key={
                        typeof option === "string"
                          ? option
                          : option.id || option.license_number
                      }
                    >
                      {label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name"
                    size="large"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {firstCard.loadingOptions ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Select Contractor Button */}
          {firstCard.selectedContractor && !firstCard.showDetails && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Button
                variant="contained"
                disabled={checkingLicense}
                onClick={async () => {
                  const contractor = firstCard.selectedContractor;
                  const licenseNumber = contractor.license_number || "";

                  if (!licenseNumber || !licenseNumber.trim()) {
                    setLicenseNumberError("License number is required");
                    return;
                  }

                  // Check if license number already exists
                  // This will check both addedCustomers and the database
                  const exists = await checkLicenseExists(licenseNumber);

                  if (exists) {
                    // Don't proceed if license exists - error is already set in checkLicenseExists
                    return;
                  }

                  // Only proceed if license doesn't exist and check completed successfully
                  // Clear any previous errors
                  setLicenseNumberError(null);

                  // Proceed with showing details
                  setFirstCard((prev) => ({
                    ...prev,
                    showDetails: true,
                    licenseNumber: licenseNumber,
                    dbaName: contractor.dba_name || "",
                    firstName:
                      contractor.first_name ||
                      contractor.primary_name?.split(" ")[0] ||
                      "",
                    lastName:
                      contractor.last_name ||
                      contractor.primary_name?.split(" ").slice(1).join(" ") ||
                      "",
                    license_type: contractor.license_type || "",
                    rank: contractor.rank || "",
                    county: contractor.county || "",
                    address1:
                      contractor.address_parts?.part_1 ||
                      contractor.address ||
                      "",
                    address2: contractor.address_parts?.part_2 || "",
                    city:
                      contractor.address_parts?.city || contractor.city || "",
                    state:
                      contractor.address_parts?.state || contractor.state || "",
                    zip: contractor.address_parts?.zip || contractor.zip || "",
                    country: contractor.country || "",
                    reg_date: {
                      effective: contractor.reg_date?.effective || "",
                      expiration: contractor.reg_date?.expiration || "",
                    },
                    status: {
                      secondary: contractor.status?.secondary || "",
                    },
                  }));
                }}
              >
                {checkingLicense ? "Checking..." : "Select Contractor"}
              </Button>
            </Box>
          )}

          {/* Details Fields - Collapsible */}
          <Collapse in={firstCard.showDetails}>
            <Card
              variant="outlined"
              sx={{
                mb: 3,
                borderColor: "#d3d3d3",
                borderWidth: "2px",
                boxShadow: "2px 6px 12px rgba(0, 0, 0, 0.5) !important",
              }}
            >
              <CardContent>
                <Box>
                  <Typography
                    variant="h6"
                    align="center"
                    color="primary"
                    gutterBottom
                  >
                    Contractor Details
                  </Typography>
                </Box>
                {/* Registration Date, Expiration Date, and Status Info */}
                <Box
                  sx={{
                    my: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: "rgba(223, 232, 255, 0.5)",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Registration Date:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {firstCard.reg_date.effective || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Expiration Date:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {firstCard.reg_date.expiration || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        color={
                          firstCard.status.secondary === "Active"
                            ? "success.main"
                            : firstCard.status.secondary === "Inactive"
                              ? "error.main"
                              : "text.primary"
                        }
                      >
                        {firstCard.status.secondary || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                  {firstCard.status.secondary === "Inactive" && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      This contractor cannot be added as inactive.
                    </Alert>
                  )}
                </Box>
                {(() => {
                  const isInactive = firstCard.status.secondary === "Inactive";
                  return (
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        border: isInactive ? "2px solid #f44336" : "none",
                        borderRadius: 1,
                        p: isInactive ? 2 : 0,
                        mb: isInactive ? 2 : 0,
                      }}
                    >
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          disabled
                          size="small"
                          fullWidth
                          label="DBA Name"
                          value={firstCard.dbaName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          disabled
                          size="small"
                          fullWidth
                          label="License Type"
                          value={firstCard.license_type || ""}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          disabled
                          size="small"
                          fullWidth
                          label="First Name"
                          value={firstCard.firstName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          disabled
                          size="small"
                          fullWidth
                          label="Last Name"
                          value={firstCard.lastName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          size="small"
                          fullWidth
                          type="email"
                          label="Email"
                          value={firstCard.email}
                          disabled={isInactive || checkingEmail}
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setEmailError(null); // Clear email error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }));
                          }}
                          required
                          error={
                            (firstCard.email.trim() !== "" &&
                              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                firstCard.email,
                              )) ||
                            addedCustomers.some(
                              (customer) =>
                                customer.email?.toLowerCase().trim() ===
                                firstCard.email.toLowerCase().trim(),
                            ) ||
                            !!emailError
                          }
                          helperText={
                            firstCard.email.trim() !== "" &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(firstCard.email)
                              ? "Invalid email"
                              : addedCustomers.some(
                                    (customer) =>
                                      customer.email?.toLowerCase().trim() ===
                                      firstCard.email.toLowerCase().trim(),
                                  )
                                ? "This email has already been added"
                                : emailError
                                  ? emailError
                                  : " "
                          }
                          slotProps={{
                            input: {
                              endAdornment: checkingEmail ? (
                                <CircularProgress size={20} />
                              ) : null,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          disabled
                          size="small"
                          fullWidth
                          label="Rank"
                          value={firstCard.rank || ""}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          select
                          label="Plan Type"
                          value={firstCard.planType}
                          disabled={isInactive}
                          onChange={(e) =>
                            setFirstCard((prev) => ({
                              ...prev,
                              planType: e.target.value,
                            }))
                          }
                          required
                        >
                          {planTypes.map((plan) => (
                            <MenuItem key={plan} value={plan}>
                              {plan}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          // sx={{ mb: 1 }}
                        >
                          Address Information
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Address 1"
                          value={firstCard.address1 || ""}
                          disabled={isInactive}
                          required
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              address1: e.target.value,
                            }));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Address 2"
                          value={firstCard.address2}
                          disabled={isInactive}
                          onChange={(e) =>
                            setFirstCard((prev) => ({
                              ...prev,
                              address2: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="City"
                          value={firstCard.city}
                          disabled={isInactive}
                          required
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="State"
                          value={firstCard.state}
                          disabled={isInactive}
                          required
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              state: e.target.value,
                            }));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="ZIP"
                          value={firstCard.zip}
                          disabled={isInactive}
                          required
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              zip: e.target.value,
                            }));
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="County"
                          value={firstCard.county}
                          disabled={isInactive}
                          required
                          onChange={(e) => {
                            setSubmitError(null); // Clear error when user types
                            setFirstCard((prev) => ({
                              ...prev,
                              county: e.target.value,
                            }));
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                })()}

                {/* Add Button - Centered */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                      setLicenseNumberError(null); // Clear error when canceling
                      setEmailError(null); // Clear email error when canceling
                      setFirstCard({
                        licenseNumberInput: "",
                        nameInput: "",
                        selectedContractor: null,
                        contractorOptions: [],
                        loadingOptions: false,
                        showDetails: false,
                        licenseNumber: "",
                        dbaName: "",
                        firstName: "",
                        lastName: "",
                        middleName: "",
                        license_type: "",
                        rank: "",
                        county: "",
                        address1: "",
                        address2: "",
                        city: "",
                        state: "",
                        zip: "",
                        country: "",
                        email: "",
                        planType: "Free",
                        reg_date: {
                          effective: "",
                          expiration: "",
                        },
                        status: {
                          secondary: "",
                        },
                      });
                    }}
                    sx={{ minWidth: "10%" }}
                  >
                    Cancel Selection
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddToGrid}
                    disabled={
                      !firstCard.licenseNumber.trim() ||
                      !firstCard.email.trim() ||
                      !firstCard.address1.trim() ||
                      !firstCard.city.trim() ||
                      !firstCard.state.trim() ||
                      !firstCard.zip.trim() ||
                      !firstCard.county.trim() ||
                      firstCard.status.secondary === "Inactive" ||
                      checkingEmail ||
                      addedCustomers.some(
                        (customer) =>
                          customer.licenseNumber?.toUpperCase().trim() ===
                          firstCard.licenseNumber.toUpperCase().trim(),
                      ) ||
                      addedCustomers.some(
                        (customer) =>
                          customer.email?.toLowerCase().trim() ===
                          firstCard.email.toLowerCase().trim(),
                      ) ||
                      !!emailError
                    }
                    sx={{ minWidth: "10%" }}
                  >
                    Add Customer
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </CardContent>
      </Card>

      {/* DataGrid for Added Customers */}
      {addedCustomers.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Added Customers ({addedCustomers.length})
            </Typography>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={addedCustomers}
                columns={columns}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Submit Button - Fixed to Bottom */}
      {addedCustomers.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
            p: 2,
            backgroundColor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
          }}
        >
          {submitError && (
            <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
              {submitError}
            </Alert>
          )}
          <LoadingButton
            loading={submitLoading}
            variant="contained"
            onClick={handleSubmitCustomers}
            disabled={submitLoading || firstCard.showDetails}
            size="large"
          >
            Submit ({addedCustomers.length} customers)
          </LoadingButton>
        </Box>
      )}

      {/* Spacer to prevent content from being hidden behind fixed button */}
      {addedCustomers.length > 0 && <Box sx={{ height: 100 }} />}
    </Box>
  );
};

export default SalesCustomers;
